import { useState } from 'react';
import { useCommissionPolicy, useMarketConfigs } from '../../features/settings';
import { formatDateTime } from '../../lib/format';
import { RewardMarketSelect } from './RewardMarketSelect';

const LEVELS = [
  { key: 'level1' as const, label: '直接邀请好友' },
  { key: 'level2' as const, label: '二级邀请' },
  { key: 'level3' as const, label: '三级邀请' },
] as const;

type LevelKey = 'level1' | 'level2' | 'level3';

export function CommissionPolicyPage() {
  const markets = useMarketConfigs();
  const [selectedMarketId, selectMarket] = useState('CN');
  const market = markets.items.find((item) => item.market_id === selectedMarketId);
  const exponent = market?.currency_exponent ?? 2;
  const unit = market?.currency ?? '';
  const { data, loading, saving, error, form, updateField, save, reload } = useCommissionPolicy(selectedMarketId);

  if (loading || markets.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error || markets.error) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-red-500 mb-3">{error || markets.error}</p>
        <button onClick={() => { reload(); markets.reload(); }} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  const fenFieldKey = (level: LevelKey) => `${level}FixedFen` as const;
  const pctFieldKey = (level: LevelKey) => `${level}Percentage` as const;
  const yuanToFen = (value: string): number => {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    return Math.round(amount * 10 ** exponent);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <RewardMarketSelect markets={markets.items} value={selectedMarketId} disabled={saving} onChange={selectMarket} />
      <p className="text-sm text-gray-500">
        仅对所选地区 App 用户生效。固定奖励在被邀请人首次成功提现后发放一次；任务返佣比例在每个有效任务结算时持续生效，设置为 0 则不发放该项奖励。
      </p>

      <div className="space-y-6">
        {LEVELS.map(({ key, label }) => (
          <div key={key} className="space-y-3 rounded-md border border-gray-200 p-4">
            <h3 className="text-base font-medium text-gray-900">{label}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {key === 'level1' ? '邀请好友固定奖励金额' : '固定奖励金额'}（{unit}）
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    step={10 ** -exponent}
                    value={(form[fenFieldKey(key)] / 10 ** exponent).toFixed(exponent)}
                    onChange={(e) => updateField(fenFieldKey(key), yuanToFen(e.target.value))}
                    className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {unit}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  任务返佣比例（%）
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={form[pctFieldKey(key)]}
                  onChange={(e) => updateField(pctFieldKey(key), Number(e.target.value) || 0)}
                  className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <button
          onClick={save}
          disabled={saving || !market}
          className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
        >
          {saving && (
            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
          保存
        </button>
      </div>

      {data && (
        <div className="pt-4 border-t border-gray-200 text-xs text-gray-400 space-y-1">
          <p>最后更新：{formatDateTime(data.updated_at)}</p>
          {data.updated_by != null && <p>操作人 ID：{data.updated_by}</p>}
        </div>
      )}
    </div>
  );
}
